import { AccountsUsersPage } from '@access-management/accounts-users-page/accounts-users-page';
import { Configuration } from '@access-management/configuration/configuration';
import { GroupsTableComponent as GroupsTable } from '@access-management/groups-table/groups-table';
import { HouseholdDetailPage } from '@access-management/household-detail/household-detail-page';
import { InvitationsTableComponent as InvitationsTable } from '@access-management/invitations-table/invitations-table';
import { AccessManagementReportsComponent as AccessManagementReports } from '@access-management/reports/reports';
import { UserDetailPage } from '@access-management/user-detail/user-detail-page';
import { Routes } from '@angular/router';
import { NotAuthorized } from '@authentication/not-authorized/not-authorized';
import { CreateGeofenceForm } from '@geofences/create-geofence-form/create-geofence-form';
import { EditGeofenceForm } from '@geofences/edit-geofence-form/edit-geofence-form';
import { GeofencesList } from '@geofences/geofences-list/geofences-list';
import { FeaturesTableComponent as FeaturesTable } from '@membership/features-table/features-table';
import { OptionsTableComponent as OptionsTable } from '@membership/options-table/options-table';
import { PackagesTableComponent as PackagesTable } from '@membership/packages-table/packages-table';
import { MembershipReportsComponent as MembershipReports } from '@membership/reports/reports';
import { MenuItemDetailPage } from '@menu-items/menu-item-detail/menu-item-detail';
import { MenuItemsPendingList } from '@menu-items/menu-items-pending-list/menu-items-pending-list';
import { PaymentsList } from '@payments/payments-list/payments-list';
import { FulfillmentWindowSettingsTable } from '@producers/fulfillment-window-settings/fulfillment-window-settings-table/fulfillment-window-settings-table';
import { ProducerDetails } from '@producers/producer-details/producer-details';
import { ProducersList } from '@producers/producers-list/producers-list';

import { authGuard } from '@core/guards/auth-guard';
import { dayControlsGuard } from '@core/guards/day-controls-guard';
import { diagnosticsGuard } from '@core/guards/diagnostics-guard';
import { masGuard } from '@core/guards/mas-guard';
import { DashboardCards } from '@dashboard/dashboard-cards';
import { OrdersConfiguration } from '@orders/configuration/configuration';
import { OrderDetails } from '@orders/order-details/order-details';
import { OrdersList } from '@orders/orders-list/orders-list';

export const routes: Routes = [
  {
    path: '',
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', component: DashboardCards },
      { path: 'geofences', component: GeofencesList },
      { path: 'geofences/create', component: CreateGeofenceForm },
      { path: 'geofences/edit/:id', component: EditGeofenceForm },
      { path: 'menu-items', component: MenuItemsPendingList },
      { path: 'menu-items/:entreeId', component: MenuItemDetailPage },
      {
        path: 'orders',
        children: [
          { path: '', component: OrdersList },
          { path: 'configuration', component: OrdersConfiguration },
          { path: ':id', component: OrderDetails }
        ]
      },
      { path: 'payments', component: PaymentsList },
      {
        path: 'producers',
        children: [
          { path: '', component: ProducersList },
          { path: 'fulfillment-window-settings', component: FulfillmentWindowSettingsTable },
          {
            path: 'pricing-floors',
            loadComponent: () =>
              import('@producers/pricing-floors/pricing-floors-table/pricing-floors-table').then(
                (m) => m.PricingFloorsTable
              )
          },
          {
            path: 'add-on-settings',
            loadComponent: () =>
              import('@producers/ice-pack-settings/ice-pack-settings-table/ice-pack-settings-table').then(
                (m) => m.IcePackSettingsTable
              )
          },
          {
            path: 'add-on-stock',
            loadComponent: () =>
              import('@producers/add-on-stock/add-on-stock-table/add-on-stock-table').then((m) => m.AddOnStockTable)
          },
          {
            path: 'entree-inventory',
            loadComponent: () =>
              import('@producers/entree-inventory/entree-inventory-table/entree-inventory-table').then(
                (m) => m.EntreeInventoryTable
              )
          },
          {
            path: 'aggregation-facilities',
            loadComponent: () =>
              import('@producers/aggregation-facilities/aggregation-facilities-table/aggregation-facilities-table').then(
                (m) => m.AggregationFacilitiesTable
              )
          },
          { path: ':id', component: ProducerDetails }
        ]
      },
      {
        path: 'membership',
        children: [
          { path: '', redirectTo: 'packages', pathMatch: 'full' },
          { path: 'packages', component: PackagesTable },
          { path: 'options', component: OptionsTable },
          { path: 'features', component: FeaturesTable },
          { path: 'reports', component: MembershipReports }
        ]
      },
      {
        path: 'access-management',
        children: [
          { path: '', redirectTo: 'accounts-users', pathMatch: 'full' },
          { path: 'accounts-users', component: AccountsUsersPage },
          { path: 'groups', component: GroupsTable },
          { path: 'households/:groupId', component: HouseholdDetailPage },
          { path: 'invitations', component: InvitationsTable },
          { path: 'users/:userId', component: UserDetailPage },
          { path: 'reports', component: AccessManagementReports },
          { path: 'configuration', component: Configuration }
        ]
      },
      {
        path: 'ingredients',
        loadChildren: () => import('@ingredients/ingredients.routes').then((m) => m.routes)
      },
      {
        path: 'entitlements',
        loadChildren: () => import('@entitlements/entitlements-routes').then((m) => m.routes)
      },
      {
        path: 'notification-rules',
        loadChildren: () => import('@notification-rules/notification-rules.routes').then((m) => m.routes)
      },
      {
        path: 'recommendation-simulator',
        loadChildren: () => import('@recommendation-simulator/recommendation-simulator.routes').then((m) => m.routes)
      },
      {
        path: 'diagnostics',
        canMatch: [diagnosticsGuard],
        loadChildren: () => import('@diagnostics/diagnostics.routes').then((m) => m.routes)
      },
      {
        path: 'agent-messages',
        canMatch: [masGuard],
        loadChildren: () => import('@mas/mas.routes').then((m) => m.routes)
      },
      {
        path: 'day-controls',
        canMatch: [dayControlsGuard],
        loadChildren: () => import('@day-controls/day-controls.routes').then((m) => m.routes)
      },
      {
        path: 'login',
        redirectTo: '/dashboard'
      },
      {
        path: 'logout',
        redirectTo: '/not-authorized'
      }
    ]
  },
  {
    path: 'not-authorized',
    component: NotAuthorized
  },
  {
    path: '**',
    redirectTo: '/dashboard'
  }
];
